import { ChangeDetectionStrategy, Component, computed, Inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { IconComponent } from '@examdojo/core/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { startWith, switchMap, tap } from 'rxjs';
import { TAG_REPOSITORY, TagRepository } from '../../data/tag-repository';
import { Tag } from '../../data/tag.model';
import { TagBadgeComponent } from '../tag-badge/tag-badge.component';

@Component({
  selector: 'dojo-tag-selection-list',
  templateUrl: './tag-selection-list.component.html',
  styleUrl: './tag-selection-list.component.scss',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckbox,
    TagBadgeComponent,
    TranslocoModule,
    IconComponent,
  ],
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagSelectionListComponent {
  constructor(@Inject(TAG_REPOSITORY) private readonly tagRepository: TagRepository) {
    this.syncFormControlEffect().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly control = input.required<FormControl<string[]>>();
  readonly searchPlaceholder = input.required<string>();

  readonly selectedIds = signal<string[]>([]);

  readonly searchControl = new FormControl('', { nonNullable: true });

  readonly tags = toSignal(this.tagRepository.selectAll(), { initialValue: [] as Tag[] });

  readonly searchQuery = toSignal(this.searchControl.valueChanges.pipe(startWith('')), { initialValue: '' });

  readonly filteredTags = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    if (!query) {
      return this.tags();
    }
    return this.tags().filter((tag) => tag.name.toLowerCase().includes(query));
  });

  isSelected(tagId: string): boolean {
    return this.selectedIds().includes(tagId);
  }

  toggleTag(tagId: string): void {
    const current = this.selectedIds();
    const newIds = current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId];
    const control = this.control();
    if (control) {
      control.setValue(newIds);
    }
  }

  private syncFormControlEffect() {
    return toObservable(this.control).pipe(
      switchMap((control) => control.valueChanges.pipe(startWith(control.value))),
      tap((value) => this.selectedIds.set(value ?? [])),
    );
  }
}
